<script setup lang="ts">
import { PhArrowLeft, PhList } from '@phosphor-icons/vue'
import { useRouter } from 'vue-router'

import { Button } from '@/components/ui/button'
import { useAppDrawer } from '@/composables/useAppDrawer'
import { useScrollRestore } from '@/composables/useScrollRestore'

withDefaults(
  defineProps<{
    title: string
    /**
     * Passed explicitly rather than inferred from route depth. `route` is
     * reachable both from a stop's Passing-route tab and from Look-up, and on a
     * cold deep link there is no history to infer from at all — clean URLs mean
     * deep links get shared, so that case is normal, not exotic.
     */
    leading?: 'back' | 'menu'
  }>(),
  { leading: 'back' },
)

const router = useRouter()
const { openDrawer } = useAppDrawer()

/**
 * Every screen built on `AppScreen` gets back-navigation scroll restoration with
 * no per-screen code. Screens that scroll in a pane of their own instead (the
 * two Look-up tabs, the planner's results) call `useScrollRestore` themselves
 * with a distinct id.
 */
const { containerRef } = useScrollRestore()

function goBack() {
  // A cold deep link has nothing to go back to. Falling through to `track`
  // beats leaving the user on a button that does nothing — and now that URLs
  // carry no hash, shared deep links are the normal way in, not an edge case.
  //
  // Read the router's own history rather than `window.history.state`: the latter
  // is only populated by the web history implementation, so it is empty under
  // memory history and the fallback would fire on every screen in tests.
  if (router.options.history.state.back) router.back()
  else router.replace({ name: 'track' })
}
</script>

<template>
  <!-- min-h-0 on the body is what lets it scroll instead of pushing the bars
       off-screen; h-full + flex-1 alone would grow past the frame. -->
  <div class="flex h-full min-h-0 flex-col">
    <header
      class="flex h-14 shrink-0 items-center gap-2 border-b bg-background px-2 pt-[env(safe-area-inset-top)]"
    >
      <Button
        variant="ghost"
        size="icon"
        type="button"
        :aria-label="leading === 'menu' ? 'Open menu' : 'Go back'"
        @click="leading === 'menu' ? openDrawer() : goBack()"
      >
        <PhList v-if="leading === 'menu'" />
        <PhArrowLeft v-else />
      </Button>

      <h1 class="min-w-0 flex-1 truncate text-base font-bold">{{ title }}</h1>

      <slot name="trailing" />
    </header>

    <slot name="tabs" />

    <!-- overscroll-contain stops an overscroll at the end of the list chaining to
         the document, where it would fire Chrome's pull-to-refresh or iOS
         rubber-banding — which reads to the user as the page moving on its own. -->
    <main ref="containerRef" class="min-h-0 flex-1 overflow-y-auto overscroll-contain">
      <slot />
    </main>

    <footer v-if="$slots.footer" class="shrink-0 border-t pb-[env(safe-area-inset-bottom)]">
      <slot name="footer" />
    </footer>
  </div>
</template>
